/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';

import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import ScreenHeader from '../../components/common/screen-header';
import { unitVillagesService } from '../../services/unit-villages.service';
import { useMinimalUnits } from '../../hooks/use-units';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';

// Zod Validation Schema (mirrors backend UnitVillagesCreate/UpdateSchema)
const unitVillageSchema = z.object({
  unitId: z.string().min(1, 'Police Unit is required'),
  villageName: z
    .string()
    .trim()
    .min(1, 'Village name is required')
    .max(200, 'Village name must not exceed 200 characters')
    .regex(/^[a-zA-Z\s-]+$/, 'Village name may contain only alphabets, spaces and hyphens'),
  mandalName: z.string().trim().max(200, 'Mandal name must not exceed 200 characters').optional().or(z.literal('')),
  districtName: z.string().trim().max(200, 'District name must not exceed 200 characters').optional().or(z.literal('')),
});

type UnitVillageFormData = z.infer<typeof unitVillageSchema>;

const UnitVillageForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'unit-villages',
    basePath: '/unit-villages',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // Stops autosave rewriting the draft after a submit clears it (the reason the
  // "restore?" banner reappeared right after a create/update).
  const submittedRef = useRef(false);

  const DRAFT_KEY = `unit-village-draft-${isEditMode ? id : 'new'}`;

  // Draft state
  const [draftData, setDraftData] = useState<UnitVillageFormData | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<UnitVillageFormData>({
    resolver: zodResolver(unitVillageSchema),
    defaultValues: {
      unitId: '',
      villageName: '',
      mandalName: '',
      districtName: '',
    },
    mode: 'onChange',
  });

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const formValues = watch();

  // Shared, cached list of ALL units (minimal shape) — same source as the
  // personnel/user pickers: fetched once, reused from cache, and uncapped.
  const { data: units = [] } = useMinimalUnits();

  // Project scope for the Police Unit picker: choosing a project narrows the
  // unit list to that project's units (plus global ones). The mapping inherits
  // its project from the chosen unit, so this is a picker filter, not a stored
  // field on the village.
  const [scopeProjectId, setScopeProjectId] = useState<string | null>(null);
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = useMemo(
    () => accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name })),
    [accessibleProjects]
  );
  const projectLocked = accessibleProjects.length <= 1;

  // Default the scope: the mapped unit's project on edit, else the own/first
  // accessible project on create.
  useEffect(() => {
    if (scopeProjectId) return;
    if (isEditMode) {
      const u = units.find((x) => x._id === getValues('unitId'));
      if (u?.appId) { setScopeProjectId(u.appId); return; }
    }
    if (!isEditMode && accessibleProjects.length) setScopeProjectId(accessibleProjects[0]._id);
  }, [isEditMode, accessibleProjects, units, scopeProjectId, getValues]);

  // Fetch unit village data for edit mode - wait for ID decryption
  const { data: unitVillage, isLoading: fetchLoading } = useQuery({
    queryKey: ['unit-villages', id],
    queryFn: () => unitVillagesService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show village name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? unitVillage?.villageName : null);

  // Populate form when unit village data is loaded
  useEffect(() => {
    if (unitVillage && isEditMode) {
      const formData = {
        unitId: unitVillage.unitId || unitVillage.unit?._id || '',
        villageName: unitVillage.villageName || '',
        mandalName: unitVillage.mandalName || unitVillage.mandal?.mandalName || '',
        districtName: unitVillage.districtName || '',
      };
      reset(formData);
    }
  }, [unitVillage, isEditMode, reset]);

  // Draft autosave (only for create mode)
  useEffect(() => {
    if (isEditMode || !isDirty || submittedRef.current) return;

    const timer = setTimeout(() => {
      if (submittedRef.current) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
    }, 1500);

    return () => clearTimeout(timer);
  }, [formValues, isDirty, isEditMode, DRAFT_KEY]);

  // Load draft on mount (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed);
          setShowDraftBanner(true);
        } catch (error) {
          console.error('Failed to parse draft:', error);
        }
      }
    }
  }, [isEditMode, DRAFT_KEY]);

  // Mutation for create/update
  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditMode && id) {
        return unitVillagesService.update(id, data);
      }
      return unitVillagesService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit-villages'] });
      localStorage.removeItem(DRAFT_KEY);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Unit Village Mapping ${isEditMode ? 'updated' : 'created'} successfully`,
        life: 3000,
      });
      setTimeout(() => {
        navigateToList();
      }, 1000);
    },
    onError: (error: any) => {
      submittedRef.current = false;
      console.error('Error response:', error.response);
      const errorMessage = extractErrorMessage(error, `Failed to ${isEditMode ? 'update' : 'create'} mapping`);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  // Combined loading state for button disable
  const isSaving = mutation.isPending;

  const onSubmit = (data: UnitVillageFormData) => {
    submittedRef.current = true;
    localStorage.removeItem(DRAFT_KEY);
    // Note: createdBy/updatedBy are handled by backend from JWT token
    const payload: { unitId: string; villageName: string; mandalName?: string; districtName?: string } = {
      unitId: data.unitId,
      villageName: data.villageName,
    };
    if (data.mandalName && data.mandalName.trim()) payload.mandalName = data.mandalName.trim();
    if (data.districtName && data.districtName.trim()) payload.districtName = data.districtName.trim();
    mutation.mutate(payload);
  };

  const handleRestoreDraft = () => {
    if (draftData) {
      reset(draftData);
      setShowDraftBanner(false);
    }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftData(null);
    setShowDraftBanner(false);
  };

  // Dropdown options
  const unitOptions = useMemo(
    () =>
      units
        // Scope to the picked project's units (global units, with no appId, stay
        // available everywhere).
        .filter((u) => !scopeProjectId || !u.appId || u.appId === scopeProjectId)
        .map((u) => ({ value: u._id, label: u.name || u.unitCode || u._id })),
    [units, scopeProjectId]
  );

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 bg-canvas dark:bg-gray-900">
        {/* Header Section */}
        <ScreenHeader
          icon="pi pi-map-marker"
          title={isEditMode ? 'Edit Unit Village Mapping' : 'Create New Unit Village Mapping'}
          subtitle={isEditMode
            ? 'Update unit village mapping information'
            : 'Add a new village-to-unit mapping'}
          onBack={() => handleNavigate('/unit-villages')}
        />

        {/* Draft Recovery Banner */}
        {showDraftBanner && (
          <div className="mb-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <i className="pi pi-info-circle text-blue-600" />
              <span className="text-blue-800 dark:text-blue-200">
                Unsaved changes found. Restore them?
              </span>
            </div>
            <div className="flex gap-2">
              <Button label="Restore" size="small" onClick={handleRestoreDraft} />
              <Button label="Discard" size="small" severity="secondary" text onClick={handleDiscardDraft} />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ds-card p-4 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Mapping Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Project scope — filters the Police Unit picker below. Not a
                  persisted field; the mapping's project follows the chosen unit. */}
              <FormSelect
                name="scopeProject"
                label="Project"
                value={scopeProjectId || ''}
                onChange={(e) => {
                  const pid = e.target.value || null;
                  setScopeProjectId(pid);
                  // Drop a chosen unit that no longer belongs to the picked project.
                  const u = units.find((x) => x._id === getValues('unitId'));
                  if (u && u.appId && pid && u.appId !== pid) setValue('unitId', '');
                }}
                options={projectOptions}
                placeholder="Select project"
                disabled={projectLocked}
                helperText="Filters the police units to this project."
              />

              <Controller
                name="unitId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Police Unit"
                    options={unitOptions}
                    placeholder="Select a police unit"
                    error={!!errors.unitId}
                    helperText={errors.unitId?.message as string}
                    required
                    filter
                    filterPlaceholder="Search units..."
                  />
                )}
              />

              <Controller
                name="villageName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label="Village Name"
                    placeholder="Enter village name"
                    error={!!errors.villageName}
                    helperText={errors.villageName?.message as string}
                    required
                  />
                )}
              />

              <Controller
                name="mandalName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Mandal"
                    placeholder="Enter mandal name"
                    error={!!errors.mandalName}
                    helperText={errors.mandalName?.message as string}
                  />
                )}
              />

              <Controller
                name="districtName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="District"
                    placeholder="Enter district name"
                    error={!!errors.districtName}
                    helperText={errors.districtName?.message as string}
                  />
                )}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/unit-villages')}
              disabled={isSaving}
            />
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      {/* Leave Confirmation Dialog */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="UnitVillageForm.Dialog.Leave"
      />
    </>
  );
};

export default UnitVillageForm;
